import { TKey } from "@/app/translations/TKey";
import { Mcp } from "@/app/vars/Mcp";

const MCP_ENDPOINT_PATH = Mcp.ENDPOINT_PATH;

export type McpToolDoc = {
  labelKey: TKey;
  response: string;
};

export type McpToolGroup = {
  titleKey: TKey;
  tools: McpToolDoc[];
};

export const MCP_GUIDE_STEP_KEYS: TKey[] = [
  TKey.MCP_GUIDE_STEP_1,
  TKey.MCP_GUIDE_STEP_2,
  TKey.MCP_GUIDE_STEP_3,
];

export const MCP_GUIDE_USAGE_EXAMPLE_KEYS: TKey[] = [
  TKey.MCP_GUIDE_USAGE_EXAMPLE_1,
  TKey.MCP_GUIDE_USAGE_EXAMPLE_2,
  TKey.MCP_GUIDE_USAGE_EXAMPLE_3,
  TKey.MCP_GUIDE_USAGE_EXAMPLE_4,
];

function pageOf(shape: string): string {
  const indented = shape
    .split("\n")
    .map((line) => `    ${line}`)
    .join("\n");
  return `{
  items: [
${indented}
  ]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}`;
}

const PROJECT_SHAPE = `{
  id: number
  name: string
  description: string | null
  link: string | null
}`;

const OFFER_SHAPE = `{
  id: number
  name: string
  type: string | null
  problem: string | null
  promise: string | null
  price: string | null
  proof: string[]
  keyAdvantages: string[]
  project: { id: number; name: string }
}`;

const SAVE_OFFER_SHAPE = `{
  id: number
  name: string
}`;

const AVATAR_SHAPE = `{
  id: number
  uuid: string
  offerId: number
  avatarUrl: string
  name: string
  role: string
  gender: string
  ageRange: string[]
  locationRange: string[]
  educationLevel: string | null
  incomeRange: string | null
  companyType: string | null
  companySize: string | null
  market: string | null
  snapshotSummary: string | null
  priority: string | null
  dataSources: ("MANUAL" | "AI" | "CALLS" | "COMMUNITIES" | "SURVEYS" | "SUPPORT" | "COMPETITORS" | "OTHER")[]
  scoreFit: number | null
  scoreConfidence: number | null
  scorePain: number | null
  desires: string[]
  frustrations: string[]
  dreams: string[]
  fears: string[]
  costTime: string | null
  costEnergy: string | null
  costOpportunity: string | null
  currentSituation: string[]
  desiredSituation: string[]
  dailyRoutine: { time: string; text: string }[] | null
  mindset: string | null
  blockers: string[]
  falseBelief: string[]
  purchaseObjections: string[]
  objectionHandler: string[]
  privateQuotes: string[]
  publicQuotes: string[]
  dominantKeywords: string[]
  emotionalLanguage: string[]
  copyAdvice: string | null
  fitSummary: string | null
  urgencyLevel: "HIGH" | "MEDIUM" | "LOW" | null
  urgencyDescription: string | null
  purchasePotential: "HIGH" | "MEDIUM" | "LOW" | null
  purchasePotentialDescription: string | null
  idealClientCriteria: string[]
  nonIdealClientCriteria: string[]
  purchaseTriggers: string[]
  whereToFind: string[]
  voiceOfTrust: string | null
  approachAngle: string | null
}`;

const SAVE_AVATAR_SHAPE = `{
  id: number
  name: string
}`;

const UPLOAD_CALL_SHAPE = `{
  callId: number
  status: "UPLOADED" | "ANALYZING"
}`;

const CALL_SHAPE = `{
  id: number
  name: string
  type: string | null
  status: "UPLOADED" | "ANALYZING" | "ANALYZED" | "FAILED"
  callDate: string | null
  duration: number | null
  member: { id: number; name: string } | null
  project: { id: number; name: string }
  analysis: {
    scoreGlobal: number | null
    scoreCallGlobal: number | null
    scoreMarketGlobal: number | null
  } | null
}`;

const SIGNAL_SHAPE =
  "{ label: string; description?: string; level?: 0 | 1 | 2; quote?: string; reason?: string }";

const DYNAMIC_ITEM_SHAPE = "{ status: string; label: string; description: string }";

const CALL_ANALYSIS_SHAPE = `Signal = ${SIGNAL_SHAPE}
DynamicItem = ${DYNAMIC_ITEM_SHAPE}

{
  id: number
  callId: number
  summary: string
  scorePain: number | null
  scoreObjection: number | null
  scoreBuyingSignal: number | null
  scoreConversationClarity: number | null
  scoreProspectEngagement: number | null
  scoreNextStepClarity: number | null
  scoreMarketGlobal: number | null
  scoreCallGlobal: number | null
  scoreGlobal: number | null
  painPoints: Signal[] | null
  objections: Signal[] | null
  buyingSignals: Signal[] | null
  verbatims: Signal[] | null
  featureRequests: Signal[] | null
  competitorMentions: Signal[] | null
  keyMoments: {
    time: string
    type: string
    label: string
    description: string
    speaker: string
    importance: string
  }[] | null
  conversationDynamics: {
    talkBalance: DynamicItem
    prospectEngagement: DynamicItem
    clarityLevel: DynamicItem
    momentum: DynamicItem
    nextStepClarity: DynamicItem
    insights: string[]
    summary: string
  } | null
  metrics: {
    overview: {
      durationSeconds: number
      speakerCount: number
      turnCount: number
      questionCount: number
      clarificationRequestCount: number
      interruptionCount: number
      shortReplyCount: number
      shortReplyRate: number
    }
    bySpeaker: {
      speakerKey: string
      displayName: string
      role: string
      talkTimeSeconds: number
      talkRatioPercent: number
      turnCount: number
      avgWordsPerTurn: number
      avgWordsPerMinute: number
      questionCount: number
      clarificationCount: number
      shortReplyCount: number
      shortReplyRate: number
    }[]
    derived: {
      dominantSpeakerKey?: string
      dominantSpeakerRatio?: number
      talkBalanceStatus?: string
      engagementStatus?: string
      clarityStatus?: string
      nextStepStatus?: string
    }
  } | null
}`;

const CALL_INSIGHT_SHAPE = `{
  id: number
  type: "PAIN_POINT" | "OBJECTION" | "BUYING_SIGNAL" | "VERBATIM" | "FEATURE_REQUEST" | "COMPETITOR_MENTION"
  label: string
  description: string | null
  level: "LOW" | "MEDIUM" | "HIGH" | null
  quotes: Json
  callsCount: number
  callIds: number[]
  project: { id: number; name: string }
}`;

const CALL_PATTERN_SHAPE = `{
  id: number
  type: "TALK_BALANCE" | "PROSPECT_ENGAGEMENT" | "SPEECH_CLARITY" | "NEXT_STEPS" | "FRICTION_MOMENTS" | "EXCHANGE_TRENDS"
  label: string
  description: string | null
  level: "LOW" | "MEDIUM" | "HIGH" | null
  score: number | null
  occurrenceRate: number | null
  callsCount: number
  callIds: number[]
  project: { id: number; name: string }
}`;

const CRITERIA_SHAPE = `{
  avatarId: number
  needUpdate: boolean
  title: string | null
  searchMode: "precise" | "broad" | null
  targetRoles: string[]
  targetSeniorities: string[]
  targetFunctions: string[]
  targetIndustries: string[]
  targetBusinessModels: string[]
  targetCompanyStages: string[]
  targetCompanySizes: string[]
  targetTechnologies: string[]
  targetCompanies: string[]
  targetLocations: string[]
  headlineKeywords: string[]
  searchKeywords: string[]
  searchQueries: string[]
  mustHaveSignals: string[]
  preferredSignals: string[]
  painSignals: string[]
  triggerSignals: string[]
  trustSignals: string[]
  excludeSignals: string[]
  excludeKeywords: string[]
  excludeCompanies: string[]
  recommendedPlatforms: string[]
}`;

const SAVE_CRITERIA_SHAPE = `{
  avatarId: number
  title: string | null
}`;

const SEQUENCE_OBJECTIVE_SHAPE = `{
  id: number
  label: string
  description: string | null
  position: number
  channels: ("LINKEDIN" | "EMAIL" | "WHATSAPP")[]
}`;

const SEQUENCE_SHAPE = `{
  id: number
  objectiveId: number
  channel: "LINKEDIN" | "EMAIL" | "WHATSAPP"
  objective: {
    label: string
    description: string | null
  }
  aiRules: string | null
  needUpdate: boolean
  steps: {
    id: number
    type: string
    delay: number
    delayUnit: "MINUTE" | "HOUR" | "DAY"
    variants: {
      id: number
      label: string | null
      template: string
      position: number
      aiRules: string | null
    }[]
  }[]
}`;

const SAVE_SEQUENCE_SHAPE = `{
  objectiveId: number
  objective: string
  channel: "LINKEDIN" | "EMAIL" | "WHATSAPP"
  stepCount: number
}`;

export const MCP_GUIDE_TOOL_GROUPS: McpToolGroup[] = [
  {
    titleKey: TKey.MCP_GUIDE_GROUP_GENERAL,
    tools: [{ labelKey: TKey.MCP_GUIDE_TOOL_PROJECTS, response: pageOf(PROJECT_SHAPE) }],
  },
  {
    titleKey: TKey.MCP_GUIDE_GROUP_OFFERS,
    tools: [
      { labelKey: TKey.MCP_GUIDE_TOOL_OFFERS, response: pageOf(OFFER_SHAPE) },
      { labelKey: TKey.MCP_GUIDE_TOOL_SAVE_OFFER, response: SAVE_OFFER_SHAPE },
    ],
  },
  {
    titleKey: TKey.MCP_GUIDE_GROUP_CLIENT_AVATAR,
    tools: [
      { labelKey: TKey.MCP_GUIDE_TOOL_SAVE_AVATAR, response: SAVE_AVATAR_SHAPE },
      { labelKey: TKey.MCP_GUIDE_TOOL_AVATAR, response: AVATAR_SHAPE },
    ],
  },
  {
    titleKey: TKey.MCP_GUIDE_GROUP_CALL_ANALYSIS,
    tools: [
      { labelKey: TKey.MCP_GUIDE_TOOL_UPLOAD_CALL, response: UPLOAD_CALL_SHAPE },
      { labelKey: TKey.MCP_GUIDE_TOOL_CALLS, response: pageOf(CALL_SHAPE) },
      { labelKey: TKey.MCP_GUIDE_TOOL_CALL_ANALYSIS, response: CALL_ANALYSIS_SHAPE },
      { labelKey: TKey.MCP_GUIDE_TOOL_CALL_INSIGHTS, response: pageOf(CALL_INSIGHT_SHAPE) },
      { labelKey: TKey.MCP_GUIDE_TOOL_CALL_PATTERNS, response: pageOf(CALL_PATTERN_SHAPE) },
    ],
  },
  {
    titleKey: TKey.MCP_GUIDE_GROUP_PROSPECT_SEARCH,
    tools: [
      { labelKey: TKey.MCP_GUIDE_TOOL_CRITERIA, response: CRITERIA_SHAPE },
      { labelKey: TKey.MCP_GUIDE_TOOL_SAVE_CRITERIA, response: SAVE_CRITERIA_SHAPE },
    ],
  },
  {
    titleKey: TKey.MCP_GUIDE_GROUP_MESSAGES_CONTENT,
    tools: [
      {
        labelKey: TKey.MCP_GUIDE_TOOL_SEQUENCE_OBJECTIVES,
        response: pageOf(SEQUENCE_OBJECTIVE_SHAPE),
      },
      { labelKey: TKey.MCP_GUIDE_TOOL_SEQUENCE, response: SEQUENCE_SHAPE },
      { labelKey: TKey.MCP_GUIDE_TOOL_SAVE_SEQUENCE, response: SAVE_SEQUENCE_SHAPE },
    ],
  },
];

export function getMcpConnectorUrl(baseUrl: string): string {
  return `${baseUrl}${MCP_ENDPOINT_PATH}`;
}

export function buildMcpConfig(baseUrl: string): string {
  return `{
  "mcpServers": {
    "meidly": {
      "url": "${baseUrl}${MCP_ENDPOINT_PATH}",
      "headers": { "Authorization": "Bearer meidly_mcp_..." }
    }
  }
}`;
}
